import { ArrowUpRight, ArrowDownRight, Pin, PinOff } from "lucide-react";
import AddToWatchlistButton from "./AddToWatchlistButton";

const fmtPrice = (n, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0);

export default function StockSummaryCard({ quote, loading, pinned, onPin }) {
  if (loading) {
    return (
      <div
        data-testid="stock-summary-card"
        className="h-full border border-zinc-800 bg-[#121214] p-6 sm:p-8 flex flex-col gap-4"
      >
        <div className="h-3 w-32 bg-zinc-800 animate-pulse" />
        <div className="h-12 w-64 bg-zinc-800 animate-pulse" />
        <div className="h-4 w-40 bg-zinc-800 animate-pulse" />
      </div>
    );
  }
  if (!quote) return null;

  const up = (quote.change_pct ?? 0) >= 0;
  const ChangeIcon = up ? ArrowUpRight : ArrowDownRight;

  return (
    <div
      data-testid="stock-summary-card"
      className="h-full border border-zinc-800 bg-[#121214] p-6 sm:p-8 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-label mb-2">Live Quote · {quote.exchange || "US"}</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="font-mono text-2xl sm:text-3xl font-semibold tracking-tight">
              {quote.symbol}
            </div>
            <div className="text-zinc-400 text-sm sm:text-base truncate max-w-md">
              {quote.name}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <AddToWatchlistButton symbol={quote.symbol} />
          <button
            data-testid="pin-compare-btn"
            onClick={onPin}
            className={`inline-flex items-center gap-1.5 border px-3 h-9 text-[10px] font-mono uppercase tracking-widest transition-colors ${
              pinned
                ? "bg-[#FFD60A] text-black border-[#FFD60A] hover:bg-[#FFD60A]/90"
                : "border-zinc-800 text-zinc-300 hover:border-zinc-500 hover:text-white"
            }`}
            title={pinned ? "Unpin from comparison" : "Pin to comparison"}
          >
            {pinned ? <PinOff size={12} /> : <Pin size={12} />}
            {pinned ? "Pinned" : "Compare"}
          </button>
          {quote.logo ? (
            <img
              src={quote.logo}
              alt=""
              className="size-10 border border-zinc-800 bg-zinc-900 object-contain p-1"
            />
          ) : null}
        </div>
      </div>

      <div className="flex items-end justify-between mt-2 gap-6 flex-wrap">
        <div>
          <div className="text-label mb-1">Price</div>
          <div className="font-mono text-4xl sm:text-5xl font-bold tracking-tight">
            {fmtPrice(quote.price, quote.currency)}
          </div>
        </div>
        <div
          className={`flex items-center gap-1 font-mono text-base sm:text-lg ${
            up ? "text-[#00E676]" : "text-[#FF3B30]"
          }`}
        >
          <ChangeIcon size={18} />
          <span>
            {up ? "+" : ""}
            {(quote.change ?? 0).toFixed(2)}
          </span>
          <span className="opacity-70">
            ({up ? "+" : ""}
            {(quote.change_pct ?? 0).toFixed(2)}%)
          </span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 pt-4 mt-2 border-t border-zinc-900">
        <div>
          <div className="text-label">Prev Close</div>
          <div className="font-mono text-sm mt-1">{fmtPrice(quote.previous_close, quote.currency)}</div>
        </div>
        <div>
          <div className="text-label">Day High</div>
          <div className="font-mono text-sm mt-1">{fmtPrice(quote.day_high, quote.currency)}</div>
        </div>
        <div>
          <div className="text-label">Day Low</div>
          <div className="font-mono text-sm mt-1">{fmtPrice(quote.day_low, quote.currency)}</div>
        </div>
      </div>
    </div>
  );
}
